import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { SnackbarProvider } from "notistack";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import useMediaQuery from "@mui/material/useMediaQuery";
import * as Sentry from "@sentry/react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { theme } from "./theme";
import { kodakkuDarkTheme, kodakkuTheme } from "./theme.kodakku";
import { useVersionCheck } from "./hooks/useVersionCheck";
import { VerificationProvider } from "./context/VerificationContext";
import { UserProvider } from "./context/UserContext";
import { TaxonomyProvider } from "./context/TaxonomyProvider";
import { LocaleProvider } from "./context/LocaleProvider";
import { SessionProvider } from "./context/SessionProvider";
import Landing from "./pages/Landing";
import Fetching from "./pages/Fetching";
import ContentReview from "./pages/ContentReview";
import Analysing from "./pages/Analysing";
import Result from "./pages/Result";
import { OutsideHealthPage } from "./pages/NoCoverage";
import { PublicLayout } from "./components/domain/layout/PublicLayout";
import { BetaWallGate } from "./components/domain/layout/BetaWallGate";
import Account, { AccountFeedback, AccountProfile, AccountVerifications } from "./pages/Account";
import InternalLayout from "./pages/internal/InternalLayout";
import Home from "./pages/internal/Home";
import Annotation from "./pages/internal/Annotation";
import AnnotationReview from "./pages/internal/AnnotationReview";
import ImproveDocument from "./pages/internal/ImproveRanking/ImproveDocument";
import Documents from "./pages/internal/Library/Documents";
import TaxonomyGaps from "./pages/internal/Library/TaxonomyGaps";
import { CatalogueFields, CatalogueSubjects } from "./pages/internal/Library/Catalogue";
import {
  CoverageGapDetail,
  CoverageGaps,
  UncoveredClaims,
} from "./pages/internal/Library/CoverageGaps";
import FeedDetail from "./pages/internal/Library/FeedDetail";
import Feeds from "./pages/internal/Library/Feeds";
import NewFeed from "./pages/internal/Library/NewFeed";
import Rejected from "./pages/internal/Library/Rejected";
import BatchJobs from "./pages/internal/BatchJobs";
import BatchJobDetail from "./pages/internal/BatchJobs/BatchJobDetail";
import ClaimVerificationTrace from "./pages/internal/ClaimVerificationTrace";
import DocumentExtractionTrace from "./pages/internal/DocumentExtractionTrace";
import CorpusScan from "./pages/internal/CorpusScan";
import AdminUsers from "./pages/internal/Admin/AdminUsers";
import { RequireRole } from "./components/gate/RequireRole";
import { RedirectKeepingSearch } from "./components/gate/RedirectKeepingSearch";
import { StickyAlert } from "./components/primitives/feedback/StickyAlert";
import {
  MaintenanceScreen,
  useMaintenanceWindow,
} from "./components/primitives/feedback/MaintenanceScreen";
import { LoadingModalProvider } from "./components/primitives/feedback/LoadingModal";
import SignIn from "./pages/SignIn";
import JoinBeta from "./pages/JoinBeta";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ExpertInsightsLayout from "./pages/expert_insights/ExpertInsightsLayout";
import InsightList from "./pages/expert_insights/InsightList";
import InsightEditor from "./pages/expert_insights/InsightEditor";
import { ROUTES } from "./route_paths";

/** Scopes the denser Kodakku theme to every /internal/* route, in the colour scheme the browser
 *  asks for. */
function KodakkuThemeScope() {
  // Read before the first paint, so a dark browser never flashes the light theme.
  const dark = useMediaQuery("(prefers-color-scheme: dark)", { noSsr: true });
  return (
    <ThemeProvider theme={dark ? kodakkuDarkTheme : kodakkuTheme}>
      {/* The page's own background is the public theme's, which suits the light Kodakku as it
          always has; dark needs its canvas, and the browser told to draw its controls dark. */}
      {dark ? <CssBaseline enableColorScheme /> : null}
      <Outlet />
    </ThemeProvider>
  );
}

function AppShell() {
  useVersionCheck();
  const maintenanceWindow = useMaintenanceWindow();
  const { pathname } = useLocation();

  if (maintenanceWindow && !pathname.startsWith(ROUTES.internal.path())) {
    return <MaintenanceScreen window={maintenanceWindow} />;
  }

  return (
    <VerificationProvider>
      <StickyAlert />
      <Routes>
        {/* Public verification flow */}
        <Route element={<PublicLayout />}>
          {/* Behind the beta wall when it is up; a shared result stays readable */}
          <Route element={<BetaWallGate />}>
            <Route path={ROUTES.landing.pattern} element={<Landing />} />
            <Route path={ROUTES.fetching.pattern} element={<Fetching />} />
            <Route path={ROUTES.review.pattern} element={<ContentReview />} />
            <Route path={ROUTES.outsideHealth.pattern} element={<OutsideHealthPage />} />
            <Route path={ROUTES.analysing.pattern} element={<Analysing />} />
            <Route path={ROUTES.account.pattern} element={<Account />}>
              <Route index element={<Navigate to={ROUTES.account.profile.path()} replace />} />
              <Route path={ROUTES.account.profile.pattern} element={<AccountProfile />} />
              <Route
                path={ROUTES.account.verifications.pattern}
                element={<AccountVerifications />}
              />
              <Route path={ROUTES.account.feedback.pattern} element={<AccountFeedback />} />
            </Route>
          </Route>
          <Route path={ROUTES.verify.pattern} element={<Result />} />
        </Route>

        {/* Shared auth pages — no section, no auth required */}
        <Route path={ROUTES.signIn.pattern} element={<SignIn />} />
        <Route path={ROUTES.joinBeta.pattern} element={<JoinBeta />} />
        <Route path={ROUTES.forgotPassword.pattern} element={<ForgotPassword />} />
        <Route path={ROUTES.resetPassword.pattern} element={<ResetPassword />} />

        {/* Expert Insights section — public theme, expert_insights.expert role required */}
        <Route path={ROUTES.expertInsights.pattern} element={<ExpertInsightsLayout />}>
          <Route index element={<Navigate to={ROUTES.expertInsights.insights.path()} replace />} />
          <Route path={ROUTES.expertInsights.insights.pattern} element={<InsightList />} />
          <Route path={ROUTES.expertInsights.insights.new.pattern} element={<InsightEditor />} />
          <Route path={ROUTES.expertInsights.insights.edit.pattern} element={<InsightEditor />} />
        </Route>

        {/* Internal section (Kodakku) — denser theme scoped to the whole subtree */}
        <Route element={<KodakkuThemeScope />}>
          {/* Protected internal routes — InternalLayout contains the auth guard */}
          <Route path={ROUTES.internal.pattern} element={<InternalLayout />}>
            <Route index element={<Navigate to={ROUTES.internal.home.path()} replace />} />

            <Route path={ROUTES.internal.home.pattern} element={<Home />} />

            <Route path={ROUTES.internal.annotation.pattern} element={<Annotation />} />
            <Route
              path={ROUTES.internal.annotation.review.pattern}
              element={<AnnotationReview />}
            />
            <Route
              path={ROUTES.internal.annotation.improveRanking.pattern}
              element={<ImproveDocument />}
            />

            <Route
              path={ROUTES.internal.library.pattern}
              element={<Navigate to={ROUTES.internal.library.feeds.path()} replace />}
            />
            <Route path={ROUTES.internal.library.feeds.pattern} element={<Feeds />} />
            <Route
              path={ROUTES.internal.library.feeds.new.pattern}
              element={
                <RequireRole
                  role="platform.admin"
                  fallback={<Navigate to={ROUTES.internal.library.feeds.path()} replace />}
                >
                  <NewFeed />
                </RequireRole>
              }
            />
            <Route path={ROUTES.internal.library.feeds.detail.pattern} element={<FeedDetail />} />
            <Route
              path={ROUTES.internal.library.catalogue.pattern}
              element={<Navigate to={ROUTES.internal.library.catalogue.fields.path()} replace />}
            />
            <Route
              path={ROUTES.internal.library.catalogue.fields.pattern}
              element={<CatalogueFields />}
            />
            <Route
              path={ROUTES.internal.library.catalogue.subjects.pattern}
              element={<CatalogueSubjects />}
            />
            <Route
              path={ROUTES.internal.library.catalogue.documents.pattern}
              element={<Documents />}
            />
            <Route
              path={ROUTES.internal.library.catalogue.rejected.pattern}
              element={<Rejected />}
            />
            <Route
              path={ROUTES.internal.library.coverage.pattern}
              element={<Navigate to={ROUTES.internal.library.coverage.gaps.path()} replace />}
            />
            <Route
              path={ROUTES.internal.library.coverage.gaps.pattern}
              element={<CoverageGaps />}
            />
            <Route
              path={ROUTES.internal.library.coverage.gaps.detail.pattern}
              element={<CoverageGapDetail />}
            />
            <Route
              path={ROUTES.internal.library.coverage.uncoveredClaims.pattern}
              element={<UncoveredClaims />}
            />
            <Route
              path={ROUTES.internal.library.taxonomy.pattern}
              element={<Navigate to={ROUTES.internal.library.taxonomy.gaps.path()} replace />}
            />
            <Route
              path={ROUTES.internal.library.taxonomy.gaps.pattern}
              element={<TaxonomyGaps />}
            />
            <Route
              path={ROUTES.internal.library.legacyDocuments.pattern}
              element={
                <RedirectKeepingSearch to={ROUTES.internal.library.catalogue.documents.path()} />
              }
            />
            <Route
              path={ROUTES.internal.library.legacyRejected.pattern}
              element={
                <RedirectKeepingSearch to={ROUTES.internal.library.catalogue.rejected.path()} />
              }
            />
            <Route
              path={ROUTES.internal.library.legacyCoverageGaps.pattern}
              element={<RedirectKeepingSearch to={ROUTES.internal.library.coverage.gaps.path()} />}
            />
            <Route
              path={ROUTES.internal.library.legacyTaxonomyGaps.pattern}
              element={<RedirectKeepingSearch to={ROUTES.internal.library.taxonomy.gaps.path()} />}
            />
            <Route
              path={ROUTES.internal.legacyDocuments.pattern}
              element={
                <RedirectKeepingSearch to={ROUTES.internal.library.catalogue.documents.path()} />
              }
            />

            <Route path={ROUTES.internal.batchJobs.pattern} element={<BatchJobs />} />
            <Route path={ROUTES.internal.batchJobs.detail.pattern} element={<BatchJobDetail />} />

            <Route
              path={ROUTES.internal.investigations.pattern}
              element={<Navigate to={ROUTES.internal.investigations.claimTrace.path()} replace />}
            />
            <Route
              path={ROUTES.internal.investigations.claimTrace.pattern}
              element={<ClaimVerificationTrace />}
            />
            <Route
              path={ROUTES.internal.investigations.corpusScan.pattern}
              element={
                <RequireRole
                  role="platform.admin"
                  fallback={<Navigate to={ROUTES.internal.investigations.path()} replace />}
                >
                  <CorpusScan />
                </RequireRole>
              }
            />
            <Route
              path={ROUTES.internal.investigations.documentTrace.pattern}
              element={<DocumentExtractionTrace />}
            />
            <Route
              path={ROUTES.internal.investigations.documentTrace.detail.pattern}
              element={<DocumentExtractionTrace />}
            />

            <Route
              path={ROUTES.internal.admin.users.pattern}
              element={
                <RequireRole
                  role="platform.admin"
                  fallback={<Navigate to={ROUTES.internal.home.path()} replace />}
                >
                  <AdminUsers />
                </RequireRole>
              }
            />
          </Route>
        </Route>
      </Routes>
    </VerificationProvider>
  );
}

function ErrorFallback({ error, resetError }: { error: Error; resetError: () => void }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        gap: 2,
        p: 4,
      }}
    >
      <Typography variant="h6" color="error">
        Something went wrong...
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ maxWidth: 480, textAlign: "center" }}
      >
        {error.message}
      </Typography>
      <Button variant="outlined" onClick={resetError}>
        Try again
      </Button>
    </Box>
  );
}

export default function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Sentry.ErrorBoundary
          fallback={({ error, resetError }) => (
            <ErrorFallback error={error as Error} resetError={resetError} />
          )}
        >
          <SnackbarProvider>
            <SessionProvider>
              <UserProvider>
                <LocaleProvider>
                  <TaxonomyProvider>
                    <LoadingModalProvider>
                      <AppShell />
                    </LoadingModalProvider>
                  </TaxonomyProvider>
                </LocaleProvider>
              </UserProvider>
            </SessionProvider>
          </SnackbarProvider>
        </Sentry.ErrorBoundary>
      </ThemeProvider>
    </BrowserRouter>
  );
}
